<template>
  <div class="table-wrapper">
    <import-upload v-if="getterPopupName === 'Upload'" :txnOptions="txnOptions" />
    <import-confirmation v-else-if="getterPopupName === 'ImportConfirmation'"
            @refreshList="refreshAfterUpload" />
    <import-filters v-else-if="['ImportFilters', 'ImportExports'].includes(getterPopupName)"
            @updateFilters="updateFilters" :txnOptions="filterTxnOptions" />

    <div class="table-title">
      <div class="subject">
        <p>Bulk Imports</p>
        <span v-if="typeof paginate.total === 'number'">
          Total ({{paginate.total}})
        </span>
      </div>

      <div class="filters">
        <div class="options">
          <button @click="openPopup('Upload')" v-if="txnOptions.length">
            Import File
            <i class="fa fa-upload" />
          </button>
          <button @click="openPopup('ImportExports')">
            Export
            <i class="fa fa-share" />
          </button>
          <button @click="openPopup('ImportFilters')"
                  :class="{'filters-applied': filtersApplied}"
                  class="dark-btn">
            Filter
            <i class="fa fa-filter" />
          </button>
        </div>

        <import-search placeholder="Search ID / File name"
                @populateList="clearSearch" @setFunctions="setSearchFunctions"
                :triggerSearch="triggerSearch" :searchTerm="searchTerm" />
      </div>
    </div>

    <div class="table-head" v-if="isDesktop()">
      <p class="d-sm">ID</p>
      <p>File Name</p>
      <p>Transaction Type</p>
      <p>Uploaded Time</p>
      <p class="d-xl">Transaction Status</p>
      <p>Import Status</p>
    </div>

    <div class="table-body">
      <div class="table-row cursor"
        v-for="(data, index) in list" :key="index"
        @click="showDetails(data.id)"
      >
        <p class="mt-grey-text d-sm">
          <b v-if="!isDesktop()">ID:</b>
          <span>{{data.id}}</span>
        </p>
        <p>
          <b v-if="!isDesktop()">File:</b>
          <u @click="downloadFile($event, data.id, 'input')">
            <span>{{data.file_name}}</span>
          </u>
        </p>
        <p>
          <b v-if="!isDesktop()">Txn Type:</b>
          {{data.txn_type}}
        </p>
        <p class="mt-grey-text">
          {{data.created_at}}
        </p>
        <p class="d-xl">
          <span class="row">
            <span>
              <b>Processed</b>:
              <strong>{{data.initiated_txns}} / {{data.total_entries}}</strong>
            </span>
            <span>
              <b>Unprocessed</b>:
              <strong>{{data.invalid_entries}} / {{data.total_entries}}</strong>
            </span>
          </span>
          <span class="row">
            <span>
              <b>Success</b>:
              <strong>{{data.txn_stats.completed}}</strong>
            </span>
            <span>
              <b>Failed</b>:
              <strong>{{data.txn_stats.failed}}</strong>
            </span>
            <span>
              <b>Pending</b>:
              <strong>{{data.txn_stats.pending}}</strong>
            </span>
          </span>
          <span v-if="data.status !== 'VALIDATING'" class="row">
            <u @click="downloadFile($event, data.id, 'output')">Output file</u>
          </span>
        </p>
        <p class="mt-grey-text">
          {{data.status}}
          <i v-if="!['PROCESSED', 'NOT_PROCESSED'].includes(data.status)"
            class="fa fa-sync-alt" @click="refreshDetails($event, data)" />
        </p>
      </div>
    </div>

    <table-loader v-if="!paginate.data_added || list.length < paginate.total"
                  :css="{'single-side': !isDesktop()}" />

    <div class="table-empty" v-if="paginate.total === 0">
      <p v-if="searchTerm">Unable to find Imports with <span class="highlight">{{searchTerm}}</span>.<br>Please search with the complete ID / File name.<br>Search is case sensitive - please search with the correct case to get the valid results.</p>
      <p v-else>{{emptyRecords}}</p>
    </div>

    <promotion-banner />
  </div>
</template>

<script>
import ImportSearch from '@/components/global/SearchRecords'
import ImportFilters from '@/components/Imports/Filter'
import ImportUpload from '@/components/Imports/Upload'
import ImportConfirmation from '@/components/Imports/Confirmation'
import PromotionBanner from '@/components/global/PromotionBanner'

import ApiServices from '@/services/api/imports'
import ListMixins from '@/mixins/listMixins'
import { capitalizeByDelimiter } from '@/utils/helpers'

export default {
  name: 'ImportsList',
  components: { ImportUpload, ImportFilters, ImportConfirmation, ImportSearch, PromotionBanner },
  mixins: [ListMixins],
  data () {
    return {
      txnOptions: [],
      filterTxnOptions: []
    }
  },
  beforeDestroy () {
    this.SET_SECTION_DETAILS(null)
  },
  created () {
    this.startProcessing()
  },
  methods: {
    capitalizeByDelimiter,
    populateParams () {
      this.api['search'] = ApiServices.search
      this.api['fetchStatus'] = ApiServices.details
      this.paginate['type'] = 'Import'
      this.keys['list'] = 'imports'
      this.setHighlightSelectors([1, 2])
      this.func.highlightCondition = (val) => {
        return this.searchTerm === val || (this.searchTerm + '.csv') === val
      }
    },
    fetchRecords (start) {
      return new Promise(resolve => {
        let params = {...this.filterOptions}
        if (start) {
          params['offset'] = start
        }

        ApiServices.list(params).then(response => {
          this.SET_STATEMENT_FILTER(this.filterOptions)
          let data = response.data
          if (data) {
            if (start) {
              this.list.push(...data.imports)
            } else {
              this.list = data.imports
              this.filterTxnOptions = data.txn_types.map(e => {
                return {
                  text: this.capitalizeByDelimiter(e.toLowerCase()),
                  value: e
                }
              })
              this.setPaginate(data)
            }
            this.populateMeta(response)
            resolve()
          }
        })
      })
    },
    populateMeta ({privileges}) {
      this.txnOptions = []
      if (privileges.initiate_fund_transfers) {
        this.txnOptions.push({
          text: 'Bank Transfers',
          value: 'BANK_TRANSFERS'
        })
      }
      if (privileges.initiate_bank_verifications) {
        this.txnOptions.push({
          text: 'Bank Verifications',
          value: 'BANK_VERIFICATIONS'
        })
      }
    },
    refreshAfterUpload () {
      this.filterOptions = {}
      this.resetData()
      this.populateData()
    },
    showDetails (id) {
      this.$router.push({name: 'ImportProcessedTxns', params: {id}})
    },
    downloadFile (event, id, type) {
      event.stopPropagation()
      ApiServices.download(id, {file_type: type}).then(response => {
        if (response.success) {
          location.href = response.data.download_url
        } else {
          this.errorToastMsg(response.message)
        }
      })
    }
  }
}
</script>

<style scoped lang="scss">
  @import '@/assets/scss/table.scss';

  .table-row {
    p {
      .row {
        @include custom-flex(space-between);
      }

      u {
        color: $primary;
        text-decoration: none;

        &:hover {
          text-decoration: underline !important;
        }
      }

      b {
        font-weight: 600 !important;
      }

      strong {
        font-weight: 400 !important;
      }
    }
  }

  .desktop {
    .table-row {
      p {
        word-break: break-all;
      }

      .row {
        margin-bottom: 6px;

        span {
          white-space: nowrap;
        }

        &:last-child {
          margin-bottom: 0 !important;
        }

        b {
          min-width: 60px;
          margin-right: 6px;
          text-align: left;
        }
        strong {
          margin-left: 6px;
          margin-right: 12px;
        }
      }
    }
  }

  .mobile, .tablet {
    .table-row {
      grid-template-areas: "id created_time"
                            "file txn_type"
                            "txn_status txn_status"
                            "import_status import_status";

      p {
        white-space: unset !important;
        word-break: break-word;

        &:nth-child(1) {
          grid-area: id;
        }
        &:nth-child(2) {
          grid-area: file;
        }
        &:nth-child(3) {
          grid-area: txn_type;
          text-align: right;
        }
        &:nth-child(4) {
          grid-area: created_time;
          text-align: right;
        }
        &:nth-child(5) {
          grid-area: txn_status;
        }
        &:nth-child(6) {
          grid-area: import_status;
        }
      }
    }
  }
</style>
